import { mediaQueryLarge, isDesktopBreakpoint, debounce } from '@theme/utilities';

/**
 * <neuro-crew-featured> — the "Featured coach" click-to-expand enhancement for
 * the Neuro crew section (issue #171).
 *
 * PROGRESSIVE ENHANCEMENT. The section server-renders a complete strip (every
 * member's photo + name + badge + bio visible). This element enhances that strip
 * into the interactive featured layout — but ONLY on desktop (>=750px). On mobile,
 * or with no JS, the strip stands on its own; no bio is ever stranded and no
 * phantom interactive control is exposed.
 *
 * Design intent (handoff): one card featured-wide with its bio, the rest collapsed
 * to square thumbnails. Clicking a thumbnail features it in place; the previous one
 * shrinks. Section height is reserved (content-derived) so switching never reflows
 * the page; the bio fades in only AFTER the width settles so the text never rewraps
 * mid-animation. Motion is CSS-driven (the prototype's setTimeout width tween was a
 * React-runtime workaround, not needed in a real browser).
 *
 * The clickable control is a NATIVE `<button>` created around each card's frame
 * (photo + badge + name) on enhance — so keyboard activation (Enter/Space), focus,
 * and the button role all come from the platform, not hand-rolled ARIA. Creating it
 * only on the desktop enhance keeps the mobile / no-JS baseline free of any
 * interactive control. The bio sits OUTSIDE the button, so a screen reader reads it
 * as content, not as part of the button's accessible name.
 */
class NeuroCrewFeatured extends HTMLElement {
  /** @type {HTMLElement | null} */
  #root = null;
  /** @type {HTMLElement | null} */
  #grid = null;
  /** @type {HTMLElement[]} */
  #cards = [];
  #activeIndex = 0;
  #enhanced = false;
  /** Lifetime listeners (breakpoint + resize). Re-created per connect (see below). */
  #lifetime = new AbortController();
  /** Listeners that exist only while enhanced (click). */
  #interaction = /** @type {AbortController | null} */ (null);

  connectedCallback() {
    this.#root = this.closest('.neuro-crew');
    this.#grid = this.querySelector('.neuro-crew__grid--featured');
    this.#cards = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-crew-card]')]);
    if (!this.#root || !this.#grid || this.#cards.length === 0) return;

    // Fresh controller each connect: a signal aborted by a prior disconnect makes
    // addEventListener a silent no-op, so a reconnected element (e.g. theme-editor
    // re-render) would never enhance or respond to breakpoint changes again.
    this.#lifetime = new AbortController();
    const { signal } = this.#lifetime;
    if (isDesktopBreakpoint()) this.#enhance();
    mediaQueryLarge.addEventListener('change', this.#handleBreakpointChange, { signal });
    window.addEventListener('resize', this.#handleResize, { signal });
  }

  disconnectedCallback() {
    this.#handleResize.cancel();
    this.#lifetime.abort();
    this.#interaction?.abort();
  }

  /** Desktop <-> mobile crossing: enhance when we enter desktop, tear down when we leave. */
  #handleBreakpointChange = (/** @type {MediaQueryListEvent} */ event) => {
    if (event.matches) this.#enhance();
    else this.#teardown();
  };

  /** Featured widths change with the viewport, so re-measure the reserved height. */
  #handleResize = debounce(() => {
    if (this.#enhanced) this.#measure();
  }, 150);

  /** The native <button> wrapping a card's frame, once enhanced. */
  #toggleOf(card) {
    const button = card.querySelector('.neuro-crew__toggle');
    return button instanceof HTMLButtonElement ? button : null;
  }

  #enhance() {
    if (this.#enhanced || !this.#root) return;
    this.#enhanced = true;
    this.#root.classList.add('is-enhanced');

    // Wrap each frame's contents in a real <button>. Native element = free
    // keyboard/focus/role; the button exists only in this desktop-enhanced state.
    // Give it an explicit name ("Yvette, Team Coach") rather than letting the
    // content compose one — the wrapped photo's alt equals the name, so the
    // default accessible name would double it ("Yvette Yvette Team Coach").
    for (const card of this.#cards) {
      const frame = card.querySelector('[data-crew-frame]');
      if (!(frame instanceof HTMLElement) || frame.querySelector('.neuro-crew__toggle')) continue;
      const name = frame.querySelector('[data-crew-name]')?.textContent?.trim() || 'crew member';
      const role = frame.querySelector('.neuro-crew__badge')?.textContent?.trim();
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'neuro-crew__toggle';
      button.setAttribute('aria-label', role ? `${name}, ${role}` : name);
      while (frame.firstChild) button.appendChild(frame.firstChild);
      frame.appendChild(button);
    }

    this.#setActive(0);
    this.#measure();

    this.#interaction = new AbortController();
    this.addEventListener('click', this.#handleActivate, { signal: this.#interaction.signal });
  }

  #teardown() {
    if (!this.#enhanced) return;
    this.#enhanced = false;
    this.#interaction?.abort();
    this.#interaction = null;

    this.#root?.classList.remove('is-enhanced', 'is-measuring');
    if (this.#grid) this.#grid.style.minHeight = '';
    this.style.removeProperty('--crew-bio-cap');

    // Unwrap each button back into its frame, restoring the pristine strip baseline.
    for (const card of this.#cards) {
      card.classList.remove('is-active');
      const frame = card.querySelector('[data-crew-frame]');
      const button = this.#toggleOf(card);
      if (frame instanceof HTMLElement && button) {
        while (button.firstChild) frame.appendChild(button.firstChild);
        button.remove();
      }
    }
    // #enhance() always re-pins to 0; reset here so the two stay symmetric.
    this.#activeIndex = 0;
  }

  /**
   * @param {number} index
   */
  #setActive(index) {
    this.#cards.forEach((card, i) => {
      const on = i === index;
      card.classList.toggle('is-active', on);
      this.#toggleOf(card)?.setAttribute('aria-expanded', on ? 'true' : 'false');
    });
    this.#activeIndex = index;
  }

  /**
   * Reserve a static section height equal to the TALLEST featured card, so
   * switching the active member never reflows the page. Content-derived: it
   * tracks whatever bios are actually present (no magic-number floor). Also
   * publishes `--crew-bio-cap` so the reveal never clips a long bio. Both maxima
   * are taken in one pass over the cards to avoid a second forced-reflow sweep;
   * `.is-measuring` (transitions off) keeps each toggled card's height truthful.
   */
  #measure() {
    if (!this.#root || !this.#grid) return;
    const previous = this.#activeIndex;

    this.#root.classList.add('is-measuring');
    this.#grid.style.minHeight = '';
    this.style.removeProperty('--crew-bio-cap');

    let maxCard = 0;
    let maxBio = 0;
    this.#cards.forEach((card, i) => {
      this.#setActive(i);
      maxCard = Math.max(maxCard, card.offsetHeight);
      const bio = card.querySelector('.neuro-crew__bio');
      if (bio instanceof HTMLElement) maxBio = Math.max(maxBio, bio.scrollHeight);
    });

    this.#setActive(previous);
    this.#root.classList.remove('is-measuring');

    if (maxBio > 0) this.style.setProperty('--crew-bio-cap', `${Math.ceil(maxBio)}px`);
    if (maxCard > 0) this.#grid.style.minHeight = `${Math.ceil(maxCard)}px`;
  }

  /**
   * Click on a member's button features it. Native buttons already fire click on
   * Enter/Space, so no separate keyboard handler is needed.
   * @param {Event} event
   */
  #handleActivate = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest('.neuro-crew__toggle');
    if (!button || !this.contains(button)) return;
    const card = button.closest('[data-crew-card]');
    const index = this.#cards.findIndex((c) => c === card);
    if (index === -1 || index === this.#activeIndex) return;
    this.#setActive(index);
  };
}

if (!customElements.get('neuro-crew-featured')) {
  customElements.define('neuro-crew-featured', NeuroCrewFeatured);
}
